// src/modulos/Parametros/ProductRecordReportPage.js

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { auth, db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    orderBy,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    where
} from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import {
    FormModal,
    ConfirmModal,
    PageHeader,
    Toast,
    Pagination
} from '../../components/common';
import styles from '../Registers/Registers.module.css';

// Collections
const STOCK_ENTRIES_COLLECTION = 'stock-entries';
const PRODUCTS_COLLECTION = 'products';
const REPORT_HISTORY_COLLECTION = 'product-record-report-history';
const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};
const CATEGORY_COLLECTION = 'parameters/data/product-categories';

const ITEMS_PER_PAGE = 10;

// Filter options
const SITUATION_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'Ativo', label: 'Ativo' },
    { value: 'Inativo', label: 'Inativo' }
];

const ProductRecordReportPage = () => {
    // Report state
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [productRecords, setProductRecords] = useState([]);
    const [loadingRecords, setLoadingRecords] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        searchTerm: '',
        productTypeId: '',
        categoryId: '',
        situation: '',
        dateFrom: '',
        dateTo: ''
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);

    // Flag to control when to load parameter collections
    const [shouldLoadParams, setShouldLoadParams] = useState(false);

    // Fetch products
    const { data: products, loading: productsLoading } = useFirestore(PRODUCTS_COLLECTION, 'code');

    // Load parameters when modal opens
    const { data: productTypes, loading: loadingTypes } = useSubCollection(
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.parentCollection : null,
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.parentId : null,
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.subCollectionName : null,
        'name'
    );

    const { data: categories, loading: loadingCategories } = useFirestore(
        shouldLoadParams ? CATEGORY_COLLECTION : null,
        'order'
    );

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Load report history
    useEffect(() => {
        loadReports();
    }, []);

    // Load parameters when modal opens
    useEffect(() => {
        if (isModalOpen && !shouldLoadParams) {
            setShouldLoadParams(true);
        }
    }, [isModalOpen, shouldLoadParams]);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoadingReports(true);
        try {
            const q = query(
                collection(db, REPORT_HISTORY_COLLECTION),
                orderBy('generatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Error loading report history:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoadingReports(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Error deleting report:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // Fetch stock entries for products
    const fetchProductRecords = useCallback(async () => {
        if (!products) return [];

        setLoadingRecords(true);
        try {
            const productIds = products.map(p => p.id);

            if (productIds.length === 0) {
                return [];
            }

            const q = query(
                collection(db, STOCK_ENTRIES_COLLECTION),
                where('rawMaterialId', 'in', productIds),
                orderBy('createdAt', 'desc')
            );

            const snapshot = await getDocs(q);
            const entries = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const records = [];

            for (const entry of entries) {
                const product = products.find(p => p.id === entry.rawMaterialId);
                if (product) {
                    const rawMaterialsCost = product.rawMaterials?.reduce((acc, mp) => {
                        const price = parseFloat(mp.valor) || 0;
                        const quantity = parseFloat(mp.quantity) || 0;
                        const isPercentage = !mp.isSemiAcabado &&
                            !(product.categoryIds?.some(catId =>
                                ['SERIGRAFIA', 'EMBALAGEM'].includes(
                                    categories?.find(c => c.id === catId)?.name?.toUpperCase() || ''
                                )
                            ));
                        if (isPercentage) {
                            return acc + (price * quantity) / 100;
                        }
                        return acc + (price * quantity);
                    }, 0) || 0;

                    const netWeight = parseFloat(product.netWeight) || 0;
                    const totalFormulationCost = rawMaterialsCost * netWeight;
                    const markup = parseFloat(product.markup) || 1;
                    const purchasePriceWithMarkup = totalFormulationCost * markup;
                    const nationalizedPriceWithMarkup = purchasePriceWithMarkup * 1.1;

                    records.push({
                        id: entry.id,
                        productId: product.id,
                        productCode: product.code,
                        productName: product.commercialName,
                        productTypeIds: product.productTypeIds || [],
                        categoryIds: product.categoryIds || [],
                        situation: product.situation || 'Ativo',
                        registrationCode: product.code,
                        purchasePriceWithMarkup: purchasePriceWithMarkup,
                        entryDate: entry.createdAt,
                        expirationDate: entry.expirationDate || product.shelfLifeMonths ?
                            calculateExpirationDate(entry.createdAt, product.shelfLifeMonths) : null,
                        importProductName: product.importName || product.commercialName,
                        nationalizedPriceWithMarkup: nationalizedPriceWithMarkup,
                        quantity: entry.quantity,
                        lotNumber: entry.lotNumber,
                        notes: entry.notes
                    });
                }
            }

            records.sort((a, b) => {
                const dateA = a.entryDate?.seconds || 0;
                const dateB = b.entryDate?.seconds || 0;
                return dateB - dateA;
            });

            return records;
        } catch (error) {
            console.error("Error fetching product records:", error);
            return [];
        } finally {
            setLoadingRecords(false);
        }
    }, [products, categories]);

    const calculateExpirationDate = (entryDate, shelfLifeMonths) => {
        if (!entryDate || !shelfLifeMonths) return null;
        try {
            const date = entryDate.toDate ? entryDate.toDate() : new Date(entryDate);
            date.setMonth(date.getMonth() + parseInt(shelfLifeMonths));
            return date;
        } catch {
            return null;
        }
    };

    // Load product records when products change
    useEffect(() => {
        if (products && products.length > 0 && categories) {
            fetchProductRecords().then(setProductRecords);
        }
    }, [products, categories, fetchProductRecords]);

    // Apply filters to product records
    const filteredRecords = useMemo(() => {
        if (!productRecords) return [];

        let filtered = [...productRecords];

        if (filters.searchTerm) {
            const term = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(record =>
                (record.productCode || '').toLowerCase().includes(term) ||
                (record.productName || '').toLowerCase().includes(term) ||
                (record.importProductName || '').toLowerCase().includes(term) ||
                (record.lotNumber || '').toLowerCase().includes(term)
            );
        }

        if (filters.productTypeId) {
            filtered = filtered.filter(record =>
                record.productTypeIds?.includes(filters.productTypeId)
            );
        }

        if (filters.categoryId) {
            filtered = filtered.filter(record =>
                record.categoryIds?.includes(filters.categoryId)
            );
        }

        if (filters.situation) {
            filtered = filtered.filter(record => record.situation === filters.situation);
        }

        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(record => {
                const entryDate = record.entryDate?.toDate ? record.entryDate.toDate() : new Date(record.entryDate);
                return entryDate >= fromDate;
            });
        }

        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(record => {
                const entryDate = record.entryDate?.toDate ? record.entryDate.toDate() : new Date(record.entryDate);
                return entryDate <= toDate;
            });
        }

        return filtered;
    }, [productRecords, filters]);

    // Pagination
    const totalPages = Math.ceil((filteredRecords?.length || 0) / ITEMS_PER_PAGE);
    const currentRecords = (filteredRecords || []).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const formatDate = (dateValue) => {
        if (!dateValue) return '-';
        try {
            const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
            return date.toLocaleDateString('pt-BR');
        } catch {
            return '-';
        }
    };

    const formatDateTime = (dateValue) => {
        if (!dateValue) return '-';
        try {
            const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
            return date.toLocaleString('pt-BR');
        } catch {
            return '-';
        }
    };

    const formatCurrency = (value) => {
        return value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4
        });
    };

    const generateExcelReport = async (items, filtersObj, reportTitle, fileName) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Registro de Produtos');

        worksheet.mergeCells('A1:H1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = reportTitle;
        titleCell.font = { size: 18, bold: true, color: { argb: 'FF2C3E50' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 35;

        worksheet.mergeCells('A2:H2');
        worksheet.getCell('A2').value = `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
        worksheet.getCell('A2').font = { size: 10, italic: true };
        worksheet.getCell('A2').alignment = { horizontal: 'center' };
        worksheet.getRow(2).height = 22;

        worksheet.mergeCells('A3:H3');
        worksheet.getCell('A3').value = `Gerado por: ${currentUser?.email || 'Usuário'}`;
        worksheet.getCell('A3').font = { size: 10, italic: true };
        worksheet.getCell('A3').alignment = { horizontal: 'center' };
        worksheet.getRow(3).height = 22;

        const headers = [
            'Código de Cadastro',
            'Nome do Produto',
            'Preço Compra (c/ markup)',
            'Estoque Entrada (data)',
            'Validade (data)',
            'Nome do Produto Importação',
            'Preço Nacionalizado (c/ markup)',
            'Lote'
        ];

        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2C3E50' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
        worksheet.getRow(4).height = 40;

        worksheet.getColumn(1).width = 18;
        worksheet.getColumn(2).width = 35;
        worksheet.getColumn(3).width = 20;
        worksheet.getColumn(4).width = 18;
        worksheet.getColumn(5).width = 15;
        worksheet.getColumn(6).width = 35;
        worksheet.getColumn(7).width = 22;
        worksheet.getColumn(8).width = 15;

        items.forEach((record) => {
            const row = worksheet.addRow([
                record.registrationCode || '-',
                record.productName || '-',
                formatCurrency(record.purchasePriceWithMarkup || 0),
                formatDate(record.entryDate),
                formatDate(record.expirationDate),
                record.importProductName || '-',
                formatCurrency(record.nationalizedPriceWithMarkup || 0),
                record.lotNumber || '-'
            ]);

            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle', wrapText: true };
                if (colNumber !== 2 && colNumber !== 6) {
                    cell.alignment = { ...cell.alignment, horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            row.height = 30;

            if (record.expirationDate) {
                const expiryDate = record.expirationDate.toDate ? record.expirationDate.toDate() : new Date(record.expirationDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (expiryDate < today) {
                    const expiryCell = row.getCell(5);
                    expiryCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFF8D7DA' }
                    };
                    expiryCell.font = { color: { argb: 'FFC62828' } };
                }
            }
        });

        const summaryRow = worksheet.addRow([`Total de registros: ${items.length}`, '', '', '', '', '', '', '']);
        summaryRow.getCell(1).font = { bold: true };
        worksheet.mergeCells(`A${worksheet.rowCount}:H${worksheet.rowCount}`);
        worksheet.getRow(worksheet.rowCount).height = 28;

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (filteredRecords.length === 0) {
            addToast('Nenhum registro de produto encontrado com os filtros selecionados.', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            const filterDisplay = {};

            if (filters.productTypeId && productTypes) {
                const type = productTypes.find(t => t.id === filters.productTypeId);
                if (type) filterDisplay.productTypeName = type.name;
            }
            if (filters.categoryId && categories) {
                const cat = categories.find(c => c.id === filters.categoryId);
                if (cat) filterDisplay.categoryName = cat.name;
            }
            if (filters.situation) {
                filterDisplay.situationLabel = SITUATION_OPTIONS.find(s => s.value === filters.situation)?.label;
            }
            if (filters.dateFrom) {
                filterDisplay.dateFrom = new Date(filters.dateFrom).toLocaleDateString('pt-BR');
            }
            if (filters.dateTo) {
                filterDisplay.dateTo = new Date(filters.dateTo).toLocaleDateString('pt-BR');
            }
            if (filters.searchTerm) {
                filterDisplay.searchTerm = filters.searchTerm;
            }

            let reportTitle = 'REGISTRO DE PRODUTOS';
            if (filterDisplay.productTypeName) reportTitle += ` - ${filterDisplay.productTypeName}`;
            if (filterDisplay.categoryName) reportTitle += ` - ${filterDisplay.categoryName}`;

            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filterParts = [];
            if (filterDisplay.productTypeName) filterParts.push(filterDisplay.productTypeName.replace(/\s/g, '_'));
            if (filterDisplay.categoryName) filterParts.push(filterDisplay.categoryName.replace(/\s/g, '_'));
            const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
            const fileName = `Registro_Produtos${filterSuffix}_${timestamp}.xlsx`;

            await generateExcelReport(filteredRecords, filterDisplay, reportTitle, fileName);

            const reportData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                recordCount: filteredRecords.length,
                filters: {
                    searchTerm: filters.searchTerm || null,
                    productTypeId: filters.productTypeId,
                    productTypeName: filterDisplay.productTypeName || null,
                    categoryId: filters.categoryId,
                    categoryName: filterDisplay.categoryName || null,
                    situation: filters.situation,
                    situationLabel: filterDisplay.situationLabel || null,
                    dateFrom: filters.dateFrom || null,
                    dateTo: filters.dateTo || null
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), reportData);

            setReports([
                {
                    id: docRef.id,
                    ...reportData,
                    generatedAt: new Date()
                },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Error generating report:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            searchTerm: '',
            productTypeId: '',
            categoryId: '',
            situation: '',
            dateFrom: '',
            dateTo: ''
        });
        setCurrentPage(1);
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        handleResetFilters();
    };

    const isLoading = productsLoading || loadingRecords;

    // Check if any filter is active
    const hasActiveFilters = filters.productTypeId || filters.categoryId || filters.situation ||
        filters.dateFrom || filters.dateTo || filters.searchTerm;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            {(loadingTypes || loadingCategories) && (
                <div className={styles.infoBox}>
                    <span>🔄</span>
                    <span>Carregando parâmetros do sistema...</span>
                </div>
            )}

            {/* Search Term */}
            <div className={styles.formGroup}>
                <label>Busca por Código, Nome, Nome Importação ou Lote</label>
                <input
                    type="text"
                    placeholder="Digite para buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className={styles.input}
                />
            </div>

            {/* Product Type & Category Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Tipo de Produto</label>
                    <select
                        value={filters.productTypeId}
                        onChange={(e) => handleFilterChange('productTypeId', e.target.value)}
                        className={styles.select}
                        disabled={loadingTypes}
                    >
                        <option value="">Todos os tipos</option>
                        {productTypes?.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Categoria</label>
                    <select
                        value={filters.categoryId}
                        onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                        className={styles.select}
                        disabled={loadingCategories}
                    >
                        <option value="">Todas as categorias</option>
                        {categories?.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Situação</label>
                    <select
                        value={filters.situation}
                        onChange={(e) => handleFilterChange('situation', e.target.value)}
                        className={styles.select}
                    >
                        {SITUATION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Date Range Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Data de Entrada (Início)</label>
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Data de Entrada (Fim)</label>
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Preview Info */}
            <div className={styles.previewInfo}>
                📊 {filteredRecords.length} {filteredRecords.length === 1 ? 'registro encontrado' : 'registros encontrados'} com os filtros atuais
            </div>
        </>
    );

    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Gerar Relatório de Registro de Produtos"
                subtitle="Selecione os filtros desejados para o relatório"
                isLoading={generating}
                submitText={`Gerar Relatório (${filteredRecords.length})`}
                cancelText="Cancelar"
                maxWidth="860px"
            >
                {renderFilterForm()}
                <div className={styles.formActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={handleResetFilters} className={styles.cancelButton}>
                        Limpar Filtros
                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Registro de Produtos"
                actionText="Gerar Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading}
            />

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <div className={styles.activeFiltersContainer}>
                    <span className={styles.activeFiltersLabel}>Filtros ativos:</span>
                    {filters.searchTerm && (
                        <span className={styles.filterBadge}>
                            🔍 Busca: {filters.searchTerm}
                        </span>
                    )}
                    {filters.productTypeId && productTypes?.find(t => t.id === filters.productTypeId) && (
                        <span className={styles.filterBadge}>
                            🏷️ Tipo: {productTypes.find(t => t.id === filters.productTypeId)?.name}
                        </span>
                    )}
                    {filters.categoryId && categories?.find(c => c.id === filters.categoryId) && (
                        <span className={styles.filterBadge}>
                            📁 Categoria: {categories.find(c => c.id === filters.categoryId)?.name}
                        </span>
                    )}
                    {filters.situation && (
                        <span className={styles.filterBadge}>
                            📋 Situação: {SITUATION_OPTIONS.find(s => s.value === filters.situation)?.label}
                        </span>
                    )}
                    {filters.dateFrom && (
                        <span className={styles.filterBadge}>
                            📅 De: {new Date(filters.dateFrom).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                    {filters.dateTo && (
                        <span className={styles.filterBadge}>
                            📅 Até: {new Date(filters.dateTo).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredRecords?.length || 0}
                    onPageChange={setCurrentPage}
                    itemsLabel="registros"
                />
            )}

            {/* Report History Section */}
            <div className={`${styles.tableContainer} ${styles.historyTable}`}>
                <h3 className={styles.tableTitle}>
                    Histórico de Relatórios Gerados
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th>Gerado por</th>
                                <th>Registros</th>
                                <th>Filtros Aplicados</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingReports ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        <div className={styles.spinner} style={{ margin: '0 auto 8px auto' }}></div>
                                        <span>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Gerar Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id} className={styles.tableRow}>
                                        <td>{formatDateTime(report.generatedAt)}</td>
                                        <td>
                                            <span className={styles.fileName}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.userInfo}>
                                                <span className={styles.userName}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span className={styles.userEmail}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                            {report.recordCount}
                                        </td>
                                        <td className={styles.filterDescription}>
                                            {(() => {
                                                const parts = [];
                                                if (report.filters?.productTypeName) parts.push(`Tipo: ${report.filters.productTypeName}`);
                                                if (report.filters?.categoryName) parts.push(`Cat: ${report.filters.categoryName}`);
                                                if (report.filters?.situationLabel) parts.push(`Situação: ${report.filters.situationLabel}`);
                                                if (report.filters?.dateFrom) parts.push(`De: ${report.filters.dateFrom}`);
                                                if (report.filters?.dateTo) parts.push(`Até: ${report.filters.dateTo}`);
                                                if (report.filters?.searchTerm) parts.push(`Busca: ${report.filters.searchTerm}`);
                                                return parts.length > 0 ? parts.join(', ') : 'Todos os registros';
                                            })()}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteClick(report.id)}
                                                className={`${styles.actionButton} ${styles.deleteButton} ${styles.smallDeleteButton}`}
                                            >
                                                Excluir
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProductRecordReportPage;